// src/components/ExpenseManagement.js
import React, { useState, useContext, useCallback, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { API_URL } from './apiurl';
import { toast } from 'react-toastify';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import de from 'date-fns/locale/de';
import Gehaltsrechner from './Gehaltsrechner';
import { format, startOfMonth, endOfMonth, subMonths, subYears } from 'date-fns';

// Deutsche Lokalisierung für den DatePicker
registerLocale('de', de);

// Hilfsfunktionen für Datumsbereiche
const getDateRanges = () => {
  const now = new Date();
  const currentMonthStart = startOfMonth(now);
  const currentMonthEnd = endOfMonth(now);
  
  const lastMonthStart = startOfMonth(subMonths(now, 1));
  const lastMonthEnd = endOfMonth(subMonths(now, 1));
  
  const last3MonthsStart = startOfMonth(subMonths(now, 2));
  
  const thisYearStart = new Date(now.getFullYear(), 0, 1);
  
  const last12MonthsStart = subMonths(now, 11);
  
  return {
    currentMonth: {
      label: 'Aktueller Monat',
      from: currentMonthStart,
      to: currentMonthEnd
    },
    lastMonth: {
      label: 'Letzter Monat',
      from: lastMonthStart,
      to: lastMonthEnd
    },
    last3Months: {
      label: 'Letzte 3 Monate',
      from: last3MonthsStart,
      to: now
    },
    thisYear: {
      label: 'Dieses Jahr',
      from: thisYearStart,
      to: now
    },
    last12Months: {
      label: 'Letzte 12 Monate',
      from: last12MonthsStart,
      to: now
    }
  };
};

// Tab-Konfiguration
const TAB_CONFIG = [
  {
    key: 'spesen',
    label: 'Spesenabrechnung',
    endpoint: '/spesen',
    icon: '💶',
    description: 'Berechnen Sie Ihre Hotelspesen für Übernachtungen bei Kundenbesuchen.'
  },
  {
    key: 'verpflegung',
    label: 'Verpflegungsmehraufwand',
    endpoint: '/verpflegung',
    icon: '🍽️',
    description: 'Berechnen Sie Ihren Verpflegungsmehraufwand für Tage beim Kunden.'
  },
  {
    key: 'homeoffice',
    label: 'Homeoffice',
    endpoint: '/homeoffice',
    icon: '🏠',
    description: 'Berechnen Sie Ihre Homeoffice-Pauschale für Tage im Homeoffice.'
  },
];

// Benutzerdefinierter Hook für Tab-Zustände
const useTabState = (initialFromDate = null, initialToDate = null) => {
  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(initialToDate);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthlyData, setMonthlyData] = useState(null);
  const [showMonthlyBreakdown, setShowMonthlyBreakdown] = useState(false);

  return {
    fromDate,
    toDate,
    result,
    loading,
    monthlyData,
    showMonthlyBreakdown,
    setFromDate,
    setToDate,
    setResult,
    setLoading,
    setMonthlyData,
    setShowMonthlyBreakdown
  };
};

// Benutzerdefiniertes Eingabefeld für DatePicker
const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
  <button 
    type="button" 
    onClick={onClick} 
    ref={ref} 
    className="date-input"
  >
    {value || 'Datum auswählen'}
  </button>
));

// DatePicker-Komponente mit Label
const DatePickerField = ({ label, selected, onChange, disabled = false }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <DatePicker
      selected={selected}
      onChange={onChange}
      dateFormat="dd.MM.yyyy"
      locale="de"
      placeholderText="TT.MM.JJJJ"
      maxDate={new Date()}
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      required
      disabled={disabled}
      customInput={<CustomDateInput />}
      className="date-picker"
    />
  </div>
);

// Schnellauswahl-Komponente für Datumsbereiche
const QuickDateSelector = ({ onSelect, disabled = false }) => {
  const dateRanges = getDateRanges();
  
  return (
    <div className="quick-date-selector">
      <label className="form-label">Schnellauswahl:</label>
      <div className="quick-date-buttons">
        {Object.entries(dateRanges).map(([key, range]) => (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(range.from, range.to)}
            disabled={disabled}
            className="quick-date-button"
          >
            {range.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Komponente zur Anzeige der monatlichen Aufschlüsselung
const MonthlyBreakdown = ({ data, currency = '€' }) => {
  if (!data || data.length === 0) return null;
  
  const totalAmount = data.reduce((sum, item) => sum + item.amount, 0);
  
  return (
    <div className="monthly-breakdown">
      <h3 className="breakdown-title">Monatliche Aufschlüsselung</h3>
      <div className="breakdown-total">
        Gesamtbetrag: <strong>{totalAmount.toFixed(2)} {currency}</strong>
      </div>
      <div className="breakdown-table-container">
        <table className="breakdown-table">
          <thead>
            <tr>
              <th>Monat</th>
              <th>Betrag</th>
              <th>Anteil</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index}>
                <td>{item.month}</td>
                <td>{item.amount.toFixed(2)} {currency}</td>
                <td>
                  <div className="percentage-bar-container">
                    <div 
                      className="percentage-bar"
                      style={{ width: `${(item.amount / totalAmount) * 100}%` }}
                    ></div>
                    <span>{((item.amount / totalAmount) * 100).toFixed(1)}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Hauptkomponente
const ExpenseManagement = () => {
  const { auth } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);
  const [useMonthlyView, setUseMonthlyView] = useState(false);
  
  // Standardwerte mit aktuellem Monat
  const dateRanges = getDateRanges();
  const currentMonthStart = dateRanges.currentMonth.from;
  const currentMonthEnd = dateRanges.currentMonth.to;
  
  // Zustände für jeden Tab einzeln erstellen
  const spesenState = useTabState(currentMonthStart, currentMonthEnd);
  const verpflegungState = useTabState(currentMonthStart, currentMonthEnd);
  const homeofficeState = useTabState(currentMonthStart, currentMonthEnd);
  
  const tabStates = [spesenState, verpflegungState, homeofficeState];

  // Automatisches Laden der 12-Monats-Daten, wenn der Ansichtsmodus umgeschaltet wird
  useEffect(() => {
    // Prüfe, ob wir auf einem der drei Abrechnungs-Tabs sind (nicht auf dem Lohn-Tab)
    if (useMonthlyView && activeTab < TAB_CONFIG.length) {
      const fetchLast12MonthsData = async () => {
        const currentTab = TAB_CONFIG[activeTab];
        const state = tabStates[activeTab];
        
        if (!currentTab) return;
        
        const last12MonthsStart = dateRanges.last12Months.from;
        const now = new Date();
        
        try {
          state.setLoading(true);
          
          const response = await fetch(`${API_URL}${currentTab.endpoint}/monthly`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.token}`,
            },
            body: JSON.stringify({
              fromDate: last12MonthsStart,
              toDate: now,
            }),
          });
          
          if (!response.ok) {
            throw new Error(`Fehler bei ${currentTab.label}`);
          }
          
          const data = await response.json();
          state.setMonthlyData(data.monthlyBreakdown || []);
          state.setResult(data.totalAmount || 0);
          state.setShowMonthlyBreakdown(true);
        } catch (err) {
          console.error(`Fehler bei ${currentTab.label}:`, err);
          toast.error(err.message || 'Serverfehler.');
        } finally {
          state.setLoading(false);
        }
      };
      
      fetchLast12MonthsData();
    } else if (activeTab < TAB_CONFIG.length) {
      // Bei Rückkehr zur normalen Ansicht den State zurücksetzen
      const state = tabStates[activeTab];
      state.setShowMonthlyBreakdown(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMonthlyView, activeTab]);

  const handleSubmit = useCallback(
    async (tabKey, endpoint, e) => {
      e.preventDefault();
      const index = TAB_CONFIG.findIndex((t) => t.key === tabKey);
      const { fromDate, toDate, setResult, setLoading, setMonthlyData, setShowMonthlyBreakdown } = tabStates[index];

      if (!fromDate || !toDate) {
        toast.error('Bitte beide Datumsfelder ausfüllen.');
        return;
      }

      setLoading(true);
      setResult(null);
      setShowMonthlyBreakdown(false);

      try {
        // URL je nach Ansichtsmodus anpassen
        const apiUrl = useMonthlyView 
          ? `${API_URL}${endpoint}/monthly` 
          : `${API_URL}${endpoint}`;
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.token}`,
          },
          body: JSON.stringify({
            fromDate,
            toDate,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Fehler bei ${TAB_CONFIG[index].label}`);
        }

        const data = await response.json();
        
        if (useMonthlyView) {
          setMonthlyData(data.monthlyBreakdown || []);
          setResult(data.totalAmount || 0);
          setShowMonthlyBreakdown(true);
        } else {
          setResult(data.amount);
        }
        
        toast.success(`${TAB_CONFIG[index].label} erfolgreich!`);
      } catch (err) {
        console.error(`Fehler bei ${TAB_CONFIG[index].label}:`, err);
        toast.error(err.message || 'Serverfehler.');
      } finally {
        setLoading(false);
      }
    },
    [auth.token, tabStates, useMonthlyView]
  );

  // Handler für die Schnellauswahl-Buttons
  const handleQuickSelect = (tabIndex, fromDate, toDate) => {
    tabStates[tabIndex].setFromDate(fromDate);
    tabStates[tabIndex].setToDate(toDate);
  };

  return (
    <div className="expense-container">
      <h2 className="expense-title">Spesen, Verpflegungsmehraufwand, Homeoffice & Lohn</h2>
      
      <div className="view-toggle">
        <button 
          className={`view-toggle-button ${!useMonthlyView ? 'active' : ''}`}
          onClick={() => setUseMonthlyView(false)}
        >
          Individueller Zeitraum
        </button>
        <button 
          className={`view-toggle-button ${useMonthlyView ? 'active' : ''}`}
          onClick={() => setUseMonthlyView(true)}
        >
          Monatsübersicht (Letzte 12 Monate)
        </button>
      </div>

      <Tabs 
        selectedIndex={activeTab} 
        onSelect={setActiveTab}
        className="expense-tabs"
      >
        <TabList className="tab-list">
          {TAB_CONFIG.map((tab) => (
            <Tab 
              key={tab.key} 
              className="tab-header"
              selectedClassName="tab-header-selected"
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </Tab>
          ))}
          <Tab 
            className="tab-header"
            selectedClassName="tab-header-selected"
          >
            <span className="tab-icon">💰</span>
            Lohn
          </Tab>
        </TabList>

        {TAB_CONFIG.map((tab, index) => {
          const state = tabStates[index];
          
          return (
            <TabPanel key={tab.key} className="tab-panel">
              {useMonthlyView ? (
                <div className="monthly-view">
                  <div className="info-box">
                    <p>
                      <span className="info-icon">ℹ️</span>
                      In dieser Ansicht werden automatisch die Daten der letzten 12 Monate geladen und analysiert.
                    </p>
                  </div>
                  
                  {state.loading && (
                    <div className="loading-indicator">
                      <div className="spinner"></div>
                      <p>Daten werden geladen...</p>
                    </div>
                  )}
                  
                  {state.result !== null && !state.loading && (
                    <div className="result-container">
                      <div className="result-box">
                        <h3>Gesamtbetrag (letzte 12 Monate):</h3>
                        <p className="result-amount">{state.result.toFixed(2)} €</p>
                      </div>
                      
                      {state.showMonthlyBreakdown && state.monthlyData && (
                        <MonthlyBreakdown data={state.monthlyData} />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <form 
                  onSubmit={(e) => handleSubmit(tab.key, tab.endpoint, e)} 
                  className="expense-form"
                >
                  <div className="form-row">
                    <DatePickerField
                      label="Datum von:"
                      selected={state.fromDate}
                      onChange={state.setFromDate}
                      disabled={state.loading}
                    />
                    <DatePickerField
                      label="Datum bis:"
                      selected={state.toDate}
                      onChange={state.setToDate}
                      disabled={state.loading}
                    />
                  </div>
                  
                  <QuickDateSelector 
                    onSelect={(from, to) => handleQuickSelect(index, from, to)}
                    disabled={state.loading}
                  />

                  <button
                    type="submit"
                    className={`submit-button ${state.loading ? 'disabled' : ''}`}
                    disabled={state.loading}
                  >
                    {state.loading ? (
                      <>
                        <span className="button-spinner"></span>
                        Berechnung...
                      </>
                    ) : (
                      'Berechnen'
                    )}
                  </button>

                  {state.result !== null && (
                    <div className="result-box">
                      <h3>Ergebnis:</h3>
                      <p className="result-amount">{state.result.toFixed(2)} €</p>
                    </div>
                  )}
                </form>
              )}
            </TabPanel>
          );
        })}

        <TabPanel className="tab-panel">
          <Gehaltsrechner />
        </TabPanel>
      </Tabs>
      
      <style jsx>{`
        /* Allgemeine Styles */
        .expense-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          font-family: 'Roboto', sans-serif;
          background-color: #f9fafb;
          border-radius: 8px;
          box-shadow: 0 4px 14px rgba(0,0,0,0.1);
        }
        
        .expense-title {
          text-align: center;
          margin-bottom: 1.5rem;
          font-size: 1.65rem;
          color: #374151;
        }
        
        /* Umschalter für Ansicht */
        .view-toggle {
          display: flex;
          justify-content: center;
          margin-bottom: 1.5rem;
          padding: 0.25rem;
          background-color: #e5e7eb;
          border-radius: 8px;
        }
        
        .view-toggle-button {
          padding: 0.75rem 1rem;
          background: none;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.2s;
        }
        
        .view-toggle-button.active {
          background-color: #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          font-weight: 500;
          color: #2563eb;
        }
        
        /* Tab-Styles */
        .expense-tabs {
          margin-top: 1rem;
        }
        
        .tab-list {
          display: flex;
          background-color: #f3f4f6;
          border-radius: 8px;
          padding: 0.25rem;
          margin-bottom: 1.5rem !important;
        }
        
        .tab-header {
          flex: 1;
          padding: 0.75rem 0.5rem;
          text-align: center;
          cursor: pointer;
          border-radius: 6px;
          margin: 0.25rem;
          transition: all 0.2s;
          font-size: 0.9rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }
        
        .tab-header:hover {
          background-color: rgba(255, 255, 255, 0.5);
        }
        
        .tab-header-selected {
          background-color: #fff !important;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          font-weight: 500;
          color: #2563eb;
        }
        
        .tab-icon {
          font-size: 1.25rem;
          margin-bottom: 0.25rem;
        }
        
        .tab-panel {
          padding: 1rem;
          background-color: #fff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        
        .tab-description {
          margin-bottom: 1.25rem;
          padding: 0.75rem;
          background-color: #f0f9ff;
          border-left: 3px solid #3b82f6;
          border-radius: 0 4px 4px 0;
          color: #1e40af;
        }
        
        /* Formular-Styles */
        .expense-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        
        .form-row {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }
        
        .form-group {
          flex: 1;
          min-width: 200px;
        }
        
        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          color: #4b5563;
          font-weight: 500;
          font-size: 0.9rem;
        }
        
        .date-input {
          width: 100%;
          padding: 0.625rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background-color: #fff;
          cursor: pointer;
          text-align: left;
          font-size: 0.95rem;
          color: #374151;
          transition: border-color 0.2s;
        }
        
        .date-input:hover {
          border-color: #9ca3af;
        }
        
        .date-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
        }
        
        /* Schnellauswahl */
        .quick-date-selector {
          margin-top: 0.5rem;
        }
        
        .quick-date-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        
        .quick-date-button {
          padding: 0.5rem 0.75rem;
          background-color: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .quick-date-button:hover {
          background-color: #e5e7eb;
          border-color: #9ca3af;
        }
        
        /* Submit Button */
        .submit-button {
          padding: 0.75rem;
          background-color: #3b82f6;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 1rem;
          transition: background-color 0.3s;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
        }
        
        .submit-button:hover {
          background-color: #2563eb;
        }
        
        .submit-button.disabled {
          background-color: #93c5fd;
          cursor: not-allowed;
        }
        
        .button-spinner {
          width: 1rem;
          height: 1rem;
          border: 2px solid transparent;
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          display: inline-block;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Ergebnis-Darstellung */
        .result-box {
          margin-top: 1.5rem;
          padding: 1.25rem;
          background-color: #f0f9ff;
          border-radius: 6px;
          text-align: center;
        }
        
        .result-box h3 {
          margin-bottom: 0.5rem;
          color: #1e40af;
          font-size: 1.1rem;
        }
        
        .result-amount {
          font-size: 1.75rem;
          font-weight: 600;
          color: #1e40af;
        }
        
        /* Monatsansicht */
        .monthly-view {
          padding: 0.5rem;
        }
        
        .info-box {
          margin-bottom: 1.5rem;
          padding: 0.75rem 1rem;
          background-color: #fef3c7;
          border-radius: 6px;
          border-left: 3px solid #f59e0b;
        }
        
        .info-icon {
          margin-right: 0.5rem;
        }
        
        .loading-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 2rem;
        }
        
        .spinner {
          width: 2.5rem;
          height: 2.5rem;
          border: 3px solid rgba(59, 130, 246, 0.2);
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }
        
        .result-container {
          margin-top: 1rem;
        }
        
        /* Monatliche Aufschlüsselung */
        .monthly-breakdown {
          margin-top: 2rem;
          padding: 1.25rem;
          background-color: #fff;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .breakdown-title {
          text-align: center;
          margin-bottom: 1rem;
          color: #4b5563;
          font-size: 1.1rem;
        }
        
        .breakdown-total {
          text-align: center;
          margin-bottom: 1.25rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .breakdown-table-container {
          overflow-x: auto;
        }
        
        .breakdown-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .breakdown-table th {
          padding: 0.75rem;
          text-align: left;
          background-color: #f3f4f6;
          color: #4b5563;
          font-weight: 500;
          font-size: 0.9rem;
        }
        
        .breakdown-table td {
          padding: 0.75rem;
          border-top: 1px solid #e5e7eb;
        }
        
        .percentage-bar-container {
          position: relative;
          height: 1.25rem;
          background-color: #dbeafe;
          border-radius: 4px;
          overflow: hidden;
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
        }
        
        .percentage-bar {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background-color: #3b82f6;
          z-index: 1;
        }
        
        .percentage-bar-container span {
          position: relative;
          z-index: 2;
          color: #1e3a8a;
          font-size: 0.8rem;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default ExpenseManagement;